import { zodResolver } from '@hookform/resolvers/zod';
import { Currency } from '@seapoint/server/enums';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { EmployeeView, PayrollView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { FinancialInformationTable } from './financial-information-table';
import { PayrollEmployee } from './financial-information-table-column-definition';

// Schema for form validation
const payrollEmployeeSchema = z.object({
  employees: z.array(
    z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      destination: z
        .object({
          iban: z.string().nullable().optional(),
          accountNumber: z.string().nullable().optional(),
          sortCode: z.string().nullable().optional(),
          type: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      netPay: z
        .string()
        .min(1, 'Net pay is required')
        .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
          message: 'Net pay must be a valid number',
        }),
      reference: z.string().min(1, 'Reference is required'),
    }),
  ),
});

export type PayrollFormData = z.infer<typeof payrollEmployeeSchema>;

interface FinancialInformationFormProps {
  employees: EmployeeView[];
  currency: Currency;
  payrollReference?: string;
  isLoading: boolean;
  onSubmit: (data: PayrollFormData) => Promise<void>;
  currentPayroll?: PayrollView;
}

export const FinancialInformationForm = ({
  employees,
  currency,
  payrollReference,
  isLoading,
  onSubmit,
  currentPayroll,
}: FinancialInformationFormProps) => {
  const form = useForm<PayrollFormData>({
    resolver: zodResolver(payrollEmployeeSchema),
    defaultValues: {
      employees: employees.map((employee) => {
        const existingRecord = currentPayroll?.records?.find((record) => record.employeeId === employee.id);
        return {
          id: employee.id,
          firstName: employee.firstName,
          lastName: employee.lastName,
          destination: employee.destination,
          netPay: existingRecord ? existingRecord.netPay.toString() : '',
          reference: payrollReference ?? '',
        };
      }),
    },
  });

  const { handleSubmit, watch, setValue } = form;
  const watchedEmployees = watch('employees') as PayrollEmployee[];

  const handleReferenceChange = (employeeId: string, reference: string) => {
    const employeeIndex = watchedEmployees.findIndex((emp) => emp.id === employeeId);
    if (employeeIndex !== -1) {
      setValue(`employees.${employeeIndex}.reference`, reference, { shouldValidate: true });
    }
  };

  const handleNetPayChange = (employeeId: string, netPay: string) => {
    const employeeIndex = watchedEmployees.findIndex((emp) => emp.id === employeeId);
    if (employeeIndex !== -1) {
      setValue(`employees.${employeeIndex}.netPay`, netPay, { shouldValidate: true });
    }
  };

  const totalNetPay = watchedEmployees.reduce((acc, employee) => {
    if (employee.netPay && employee.netPay !== '') {
      return acc + Number(employee.netPay);
    }
    return acc;
  }, 0);

  const formattedTotalNetPay = getAmountWithCurrency({
    amount: totalNetPay,
    currency,
    isAccountBalance: true,
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-10">
        <Stack className="w-full">
          <Inline space={2} className="w-full" wrap="nowrap">
            <h2 className="text-xl font-semibold">Payroll</h2>
            <p className="text-sm text-seapoint-gray-text">({employees.length})</p>
          </Inline>

          <FinancialInformationTable
            employees={watchedEmployees}
            onReferenceChange={handleReferenceChange}
            onNetPayChange={handleNetPayChange}
          />
          <hr className="w-full" />
          <p className="mt-4 w-full pr-4 text-right text-sm">{formattedTotalNetPay}</p>
          <Inline className="mt-4 w-full justify-end">
            <Button type="submit" disabled={!form.formState.isValid} isLoading={isLoading}>
              Continue
            </Button>
          </Inline>
        </Stack>
      </form>
    </FormProvider>
  );
};
